'use client';

/**
 * AddBankAccountWizard - Add External Bank Account Flow (Sheet)
 * 
 * Two-step wizard for linking external bank accounts:
 * 1. Choose payment rail (ACH, SEPA, WIRE, SPEI, PIX)
 * 2. Fill in account details based on selected rail
 * 
 * Uses same Sheet pattern as AddFundsWizard:
 * - Mobile (<1024px): Bottom sheet, h=95vh
 * - Desktop (≥1024px): Right sheet, w=28rem
 * 
 * @see PRD-WALLET-HUB
 */

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { InlineLoadingIndicator } from '@/components/ui/inline-loading';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SFIcon } from '@/components/ui/sf-icon';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Text } from '@/components/ui/typography';
import { useIsMobile } from '@/hooks/use-mobile';
import { toast } from '@/hooks/use-toast';
import { callEdgeFunction } from '@/lib/rpc/utils/edgeFunctions';
import { sfIcons } from '@/lib/sf-icons';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { Clock } from 'lucide-react';
import { useEffect, useState } from 'react';

// Payment Rail Types
type PaymentRail = 'ACH' | 'SEPA' | 'WIRE' | 'SPEI' | 'PIX';
type WizardStep = 'choose-rail' | 'form';

interface RailInfo {
  id: PaymentRail;
  name: string;
  description: string;
  eta: string;
  countries: string[];
  currency: string;
  icon: React.ReactNode;
}

const PAYMENT_RAILS: RailInfo[] = [
  {
    id: 'ACH',
    name: 'ACH',
    description: 'US only, low fees',
    eta: '1-3 business days',
    countries: ['US'],
    currency: 'USD',
    icon: <span className="text-lg font-bold">$</span>,
  },
  {
    id: 'SEPA',
    name: 'SEPA',
    description: 'European zone transfers',
    eta: '1-2 business days',
    countries: ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'CH', 'GB'],
    currency: 'EUR',
    icon: <span className="text-lg font-bold">€</span>,
  },
  {
    id: 'WIRE',
    name: 'Wire Transfer',
    description: 'International transfers',
    eta: '1-5 business days',
    countries: ['US', 'GB', 'DE', 'FR', 'NL', 'CH', 'AE', 'SG', 'HK', 'CA', 'AU', 'NZ'],
    currency: 'USD',
    icon: <span className="text-lg font-bold">🌐</span>,
  },
  {
    id: 'SPEI',
    name: 'SPEI',
    description: 'Mexico transfers',
    eta: '1-2 business days',
    countries: ['MX'],
    currency: 'MXN',
    icon: <span className="text-lg font-bold">$</span>,
  },
  {
    id: 'PIX',
    name: 'PIX',
    description: 'Brazil instant payments',
    eta: 'Instant',
    countries: ['BR'],
    currency: 'BRL',
    icon: <span className="text-lg font-bold">R$</span>,
  },
];

// Validation patterns
const VALIDATION = {
  // ACH routing number: exactly 9 digits
  routing: /^[0-9]{9}$/,
  // IBAN: 2 letters + 2 digits + up to 30 alphanumeric
  iban: /^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/,
  // SWIFT/BIC: 8 or 11 characters
  swift: /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/,
  // CLABE: exactly 18 digits
  clabe: /^[0-9]{18}$/,
  // CPF: 11 digits
  cpf: /^[0-9]{11}$/,
  // CNPJ: 14 digits
  cnpj: /^[0-9]{14}$/,
  // Email: basic email pattern
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  // Phone: international format
  phone: /^\+?[0-9]{10,15}$/,
};

interface AddBankAccountWizardProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  bridgeCustomerId?: string | null;
}

export function AddBankAccountWizard({ open, onClose, onSuccess, bridgeCustomerId }: AddBankAccountWizardProps) {
  const isMobile = useIsMobile();

  // Wizard state
  const [step, setStep] = useState<WizardStep>('choose-rail');
  const [selectedRail, setSelectedRail] = useState<PaymentRail | null>(null);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset state when sheet closes
  useEffect(() => {
    if (!open) {
      setStep('choose-rail');
      setSelectedRail(null);
      setFormData({});
      setErrors({});
      setIsSubmitting(false);
    }
  }, [open]);

  // Handle rail selection
  const handleRailSelect = (rail: PaymentRail) => {
    setSelectedRail(rail);
    setFormData({});
    setErrors({});
    setStep('form');
  };

  // Handle input change
  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Get required fields for selected rail
  const getRequiredFields = (rail: PaymentRail): string[] => {
    switch (rail) {
      case 'ACH':
        return ['routing_number', 'account_number', 'account_type', 'holder_name'];
      case 'SEPA':
        return ['iban', 'swift_bic', 'holder_name'];
      case 'WIRE':
        return ['swift_bic', 'account_number', 'holder_name'];
      case 'SPEI':
        return ['clabe', 'holder_name'];
      case 'PIX':
        return ['pix_key', 'pix_key_type', 'holder_name'];
      default:
        return [];
    }
  };

  // Validate field
  const validateField = (field: string, value: string, rail: PaymentRail): string | null => {
    if (!value?.trim()) {
      return 'This field is required';
    }

    switch (field) {
      case 'routing_number':
        if (!VALIDATION.routing.test(value)) {
          return 'Routing number must be exactly 9 digits';
        }
        break;
      case 'iban': {
        const cleanIban = value.replace(/\s/g, '').toUpperCase();
        if (!VALIDATION.iban.test(cleanIban)) {
          return 'Invalid IBAN format';
        }
        break;
      }
      case 'swift_bic': {
        const cleanSwift = value.toUpperCase();
        if (!VALIDATION.swift.test(cleanSwift)) {
          return 'SWIFT/BIC must be 8 or 11 characters';
        }
        break;
      }
      case 'clabe':
        if (!VALIDATION.clabe.test(value.replace(/\s/g, ''))) {
          return 'CLABE must be exactly 18 digits';
        }
        break;
      case 'pix_key':
        if (formData.pix_key_type === 'cpf' && !VALIDATION.cpf.test(value.replace(/\D/g, ''))) {
          return 'CPF must be exactly 11 digits';
        }
        if (formData.pix_key_type === 'cnpj' && !VALIDATION.cnpj.test(value.replace(/\D/g, ''))) {
          return 'CNPJ must be exactly 14 digits';
        }
        if (formData.pix_key_type === 'email' && !VALIDATION.email.test(value)) {
          return 'Invalid email format';
        }
        if (formData.pix_key_type === 'phone' && !VALIDATION.phone.test(value.replace(/\D/g, ''))) {
          return 'Invalid phone number format';
        }
        break;
    }

    return null;
  };

  // Validate all fields
  const validateForm = (): boolean => {
    if (!selectedRail) return false;

    const required = getRequiredFields(selectedRail);
    const newErrors: Record<string, string> = {};

    for (const field of required) {
      const error = validateField(field, formData[field] || '', selectedRail);
      if (error) {
        newErrors[field] = error;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if form is valid (for button state)
  const isFormValid = (): boolean => {
    if (!selectedRail) return false;
    const required = getRequiredFields(selectedRail);
    return required.every(field => formData[field]?.trim());
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!selectedRail || !validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const rail = PAYMENT_RAILS.find(r => r.id === selectedRail)!;
      const requestBody = {
        customer_id: bridgeCustomerId || undefined,
        country: rail.countries[0],
        currency: rail.currency,
        rail: selectedRail,
        holder_name: formData.holder_name,
        account_owner_name: formData.holder_name,
        ...(selectedRail === 'ACH' && {
          routing_number: formData.routing_number,
          account_number: formData.account_number,
          account_type: formData.account_type as 'checking' | 'savings',
        }),
        ...(selectedRail === 'SEPA' && {
          iban: formData.iban?.replace(/\s/g, '').toUpperCase(),
          swift_bic: formData.swift_bic?.toUpperCase(),
        }),
        ...(selectedRail === 'WIRE' && {
          swift_bic: formData.swift_bic?.toUpperCase(),
          account_number: formData.account_number,
        }),
        ...(selectedRail === 'SPEI' && {
          clabe: formData.clabe?.replace(/\s/g, ''),
        }),
        ...(selectedRail === 'PIX' && {
          pix_key: formData.pix_key,
          pix_key_type: formData.pix_key_type,
        }),
      };

      const result = await callEdgeFunction('payout-route-create', requestBody);

      if (!result.ok) {
        throw new Error(result.error?.message || 'Failed to link bank account');
      }

      toast.success('Bank account linked successfully');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to link bank account');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Go back handler
  const handleBack = () => {
    if (step === 'form') {
      setStep('choose-rail');
      setSelectedRail(null);
      setFormData({});
      setErrors({});
    }
  };

  const sheetSide = isMobile ? 'bottom' : 'right';
  const showBackButton = step === 'form';

  // Get title based on step
  const getTitle = () => {
    if (step === 'choose-rail') return 'Link Bank Account';
    const rail = PAYMENT_RAILS.find(r => r.id === selectedRail);
    return rail ? `Add ${rail.name} Account` : 'Account Details';
  };

  // Get description based on step
  const getDescription = () => {
    if (step === 'choose-rail') return 'Choose payment method for withdrawals';
    const rail = PAYMENT_RAILS.find(r => r.id === selectedRail);
    return rail ? `${rail.description} • ${rail.eta}` : 'Enter your account details';
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent
        side={sheetSide}
        className={cn(
          'flex flex-col',
          isMobile
            ? 'h-[95vh] max-h-[95vh]'
            : 'w-[28rem] max-w-[28rem]',
          semanticTokens.motion.standard
        )}
      >
        {/* Header */}
        <SheetHeader className="flex-shrink-0 pb-4 border-b">
          <div className="flex items-center gap-3">
            {showBackButton && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBack}
                className="min-h-[44px] min-w-[44px] -ml-2"
                aria-label="Go back"
                disabled={isSubmitting}
              >
                <SFIcon icon={sfIcons.back} className="h-5 w-5" />
              </Button>
            )}
            <div className={cn(
              'p-2.5 rounded-xl',
              semanticTokens.bg.muted
            )}>
              <SFIcon icon={sfIcons.building2} className="h-5 w-5 text-primary" />
            </div>
            <div>
              <SheetTitle className={semanticTokens.typography.h3}>
                {getTitle()}
              </SheetTitle>
              <SheetDescription className={cn(semanticTokens.typography.caption, 'mt-0.5')}>
                {getDescription()}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain py-6 space-y-4 min-w-0">

          {/* Step: Choose Payment Rail */}
          {step === 'choose-rail' && (
            <div className="grid gap-3">
              {PAYMENT_RAILS.map((rail) => (
                <Card
                  key={rail.id}
                  className={cn(
                    'cursor-pointer transition-all',
                    semanticTokens.motion.standard,
                    'hover:shadow-md hover:border-primary/50',
                    'focus-visible:ring-2 focus-visible:ring-primary'
                  )}
                  onClick={() => handleRailSelect(rail.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleRailSelect(rail.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          'w-10 h-10 rounded-xl flex items-center justify-center',
                          semanticTokens.info.bg
                        )}>
                          {rail.icon}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <Text variant="md" className="font-medium">{rail.name}</Text>
                            <span className={cn(
                              'text-xs px-2 py-0.5 rounded-full',
                              'bg-muted text-muted-foreground'
                            )}>
                              {rail.currency}
                            </span>
                          </div>
                          <Text variant="caption" color="muted">{rail.description}</Text>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Clock className="h-3.5 w-3.5" />
                        <Text variant="caption">{rail.eta}</Text>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Step: Form */}
          {step === 'form' && selectedRail && (
            <div className="space-y-6">
              {/* Security Warning */}
              <Alert variant="warning">
                <SFIcon icon={sfIcons.security} className={cn("h-5 w-5 shrink-0", semanticTokens.warning.accent)} />
                <AlertTitle>Verify your details</AlertTitle>
                <AlertDescription>
                  Double-check all information. Incorrect details may result in failed or delayed transfers.
                </AlertDescription>
              </Alert>

              {/* ACH Form */}
              {selectedRail === 'ACH' && (
                <ACHForm
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
              )}

              {/* SEPA Form */}
              {selectedRail === 'SEPA' && (
                <SEPAForm
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
              )}

              {/* WIRE Form */}
              {selectedRail === 'WIRE' && (
                <WIREForm
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
              )}

              {/* SPEI Form */}
              {selectedRail === 'SPEI' && (
                <SPEIForm
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
              )}

              {/* PIX Form */}
              {selectedRail === 'PIX' && (
                <PIXForm
                  formData={formData}
                  errors={errors}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
              )}
            </div>
          )}

        </div>

        {/* Footer */}
        <SheetFooter className={cn(
          'flex-shrink-0 border-t pt-4 gap-3',
          isMobile && 'pb-6'
        )}>
          {step === 'form' ? (
            <>
              <Button
                variant="outline"
                className={cn('flex-1 min-h-[52px] rounded-xl')}
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                className={cn(
                  'flex-1 min-h-[52px] rounded-xl',
                  // Fix dark theme color correction - ensure proper primary colors
                  'dark:bg-primary dark:text-primary-foreground',
                  'dark:hover:bg-primary/90'
                )}
                onClick={handleSubmit}
                disabled={!isFormValid() || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <InlineLoadingIndicator size="md" className="mr-2" />
                    Linking...
                  </>
                ) : (
                  <>
                    <SFIcon icon={sfIcons.add} className="mr-2 h-5 w-5" />
                    Link Account
                  </>
                )}
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              className={cn('w-full min-h-[52px] rounded-xl')}
              onClick={onClose}
            >
              Cancel
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

// ============================================
// Form Components
// ============================================

interface FormProps {
  formData: Record<string, string>;
  errors: Record<string, string>;
  onChange: (field: string, value: string) => void;
  disabled: boolean;
}

// ACH Form (US)
function ACHForm({ formData, errors, onChange, disabled }: FormProps) {
  return (
    <div className="space-y-4">
      <FormField
        label="Routing Number"
        required
        error={errors.routing_number}
      >
        <Input
          type="text"
          value={formData.routing_number || ''}
          onChange={(e) => onChange('routing_number', e.target.value)}
          placeholder="123456789"
          className={cn("min-h-[44px]", errors.routing_number && "border-destructive")}
          disabled={disabled}
          maxLength={9}
          inputMode="numeric"
        />
        <Text variant="caption" color="muted" className="mt-1">
          9-digit ABA routing number
        </Text>
      </FormField>

      <FormField
        label="Account Number"
        required
        error={errors.account_number}
      >
        <Input
          type="text"
          value={formData.account_number || ''}
          onChange={(e) => onChange('account_number', e.target.value)}
          placeholder="0000000000"
          className={cn("min-h-[44px]", errors.account_number && "border-destructive")}
          disabled={disabled}
          inputMode="numeric"
        />
      </FormField>

      <FormField
        label="Account Type"
        required
        error={errors.account_type}
      >
        <Select
          value={formData.account_type || ''}
          onValueChange={(value) => onChange('account_type', value)}
          disabled={disabled}
        >
          <SelectTrigger className={cn("min-h-[44px]", errors.account_type && "border-destructive")}>
            <SelectValue placeholder="Select account type" />
          </SelectTrigger>
          <SelectContent className="z-[10000]">
            <SelectItem value="checking">Checking</SelectItem>
            <SelectItem value="savings">Savings</SelectItem>
          </SelectContent>
        </Select>
      </FormField>

      <FormField
        label="Account Holder Name"
        required
        error={errors.holder_name}
      >
        <Input
          type="text"
          value={formData.holder_name || ''}
          onChange={(e) => onChange('holder_name', e.target.value)}
          placeholder="John Doe"
          className={cn("min-h-[44px]", errors.holder_name && "border-destructive")}
          disabled={disabled}
        />
        <Text variant="caption" color="muted" className="mt-1">
          Must match the name on your bank account
        </Text>
      </FormField>
    </div>
  );
}

// SEPA Form (Europe)
function SEPAForm({ formData, errors, onChange, disabled }: FormProps) {
  return (
    <div className="space-y-4">
      <FormField
        label="IBAN"
        required
        error={errors.iban}
      >
        <Input
          type="text"
          value={formData.iban || ''}
          onChange={(e) => onChange('iban', e.target.value.toUpperCase())}
          placeholder="DE89370400440532013000"
          className={cn("min-h-[44px] font-mono", errors.iban && "border-destructive")}
          disabled={disabled}
        />
        <Text variant="caption" color="muted" className="mt-1">
          International Bank Account Number
        </Text>
      </FormField>

      <FormField
        label="SWIFT/BIC"
        required
        error={errors.swift_bic}
      >
        <Input
          type="text"
          value={formData.swift_bic || ''}
          onChange={(e) => onChange('swift_bic', e.target.value.toUpperCase())}
          placeholder="DEUTDEFF"
          className={cn("min-h-[44px] font-mono", errors.swift_bic && "border-destructive")}
          disabled={disabled}
          maxLength={11}
        />
        <Text variant="caption" color="muted" className="mt-1">
          8 or 11 character bank identifier code
        </Text>
      </FormField>

      <FormField
        label="Account Holder Name"
        required
        error={errors.holder_name}
      >
        <Input
          type="text"
          value={formData.holder_name || ''}
          onChange={(e) => onChange('holder_name', e.target.value)}
          placeholder="John Doe"
          className={cn("min-h-[44px]", errors.holder_name && "border-destructive")}
          disabled={disabled}
        />
      </FormField>
    </div>
  );
}

// WIRE Form (International)
function WIREForm({ formData, errors, onChange, disabled }: FormProps) {
  return (
    <div className="space-y-4">
      <FormField
        label="SWIFT/BIC"
        required
        error={errors.swift_bic}
      >
        <Input
          type="text"
          value={formData.swift_bic || ''}
          onChange={(e) => onChange('swift_bic', e.target.value.toUpperCase())}
          placeholder="DEUTDEFF"
          className={cn("min-h-[44px] font-mono", errors.swift_bic && "border-destructive")}
          disabled={disabled}
          maxLength={11}
        />
        <Text variant="caption" color="muted" className="mt-1">
          Bank identifier code for international transfers
        </Text>
      </FormField>

      <FormField
        label="Account Number"
        required
        error={errors.account_number}
      >
        <Input
          type="text"
          value={formData.account_number || ''}
          onChange={(e) => onChange('account_number', e.target.value)}
          placeholder="0000000000"
          className={cn("min-h-[44px]", errors.account_number && "border-destructive")}
          disabled={disabled}
        />
      </FormField>

      <FormField
        label="Account Holder Name"
        required
        error={errors.holder_name}
      >
        <Input
          type="text"
          value={formData.holder_name || ''}
          onChange={(e) => onChange('holder_name', e.target.value)}
          placeholder="John Doe"
          className={cn("min-h-[44px]", errors.holder_name && "border-destructive")}
          disabled={disabled}
        />
      </FormField>
    </div>
  );
}

// SPEI Form (Mexico)
function SPEIForm({ formData, errors, onChange, disabled }: FormProps) {
  return (
    <div className="space-y-4">
      <FormField
        label="CLABE"
        required
        error={errors.clabe}
      >
        <Input
          type="text"
          value={formData.clabe || ''}
          onChange={(e) => onChange('clabe', e.target.value)}
          placeholder="032180000118359719"
          className={cn("min-h-[44px] font-mono", errors.clabe && "border-destructive")}
          disabled={disabled}
          maxLength={18}
          inputMode="numeric"
        />
        <Text variant="caption" color="muted" className="mt-1">
          18-digit Mexican bank account number (CLABE Interbancaria)
        </Text>
      </FormField>

      <FormField
        label="Account Holder Name"
        required
        error={errors.holder_name}
      >
        <Input
          type="text"
          value={formData.holder_name || ''}
          onChange={(e) => onChange('holder_name', e.target.value)}
          placeholder="John Doe"
          className={cn("min-h-[44px]", errors.holder_name && "border-destructive")}
          disabled={disabled}
        />
      </FormField>
    </div>
  );
}

// PIX Form (Brazil)
function PIXForm({ formData, errors, onChange, disabled }: FormProps) {
  // Get placeholder based on PIX key type
  const getPlaceholder = () => {
    switch (formData.pix_key_type) {
      case 'cpf': return '123.456.789-00';
      case 'cnpj': return '12.345.678/0001-90';
      case 'email': return 'user@example.com';
      case 'phone': return '+5511999999999';
      case 'evp': return 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx';
      default: return 'Select PIX key type first';
    }
  };

  // Get help text based on PIX key type
  const getHelpText = () => {
    switch (formData.pix_key_type) {
      case 'cpf': return 'Enter 11-digit CPF (Brazilian Tax ID)';
      case 'cnpj': return 'Enter 14-digit CNPJ (Company Tax ID)';
      case 'email': return 'Enter email address registered as PIX key';
      case 'phone': return 'Enter phone number with country code';
      case 'evp': return 'Enter your random PIX key (EVP)';
      default: return 'Select PIX key type first';
    }
  };

  return (
    <div className="space-y-4">
      <FormField
        label="PIX Key Type"
        required
        error={errors.pix_key_type}
      >
        <Select
          value={formData.pix_key_type || ''}
          onValueChange={(value) => {
            onChange('pix_key_type', value);
            onChange('pix_key', ''); // Reset PIX key when type changes
          }}
          disabled={disabled}
        >
          <SelectTrigger className={cn("min-h-[44px]", errors.pix_key_type && "border-destructive")}>
            <SelectValue placeholder="Select PIX key type" />
          </SelectTrigger>
          <SelectContent className="z-[10000]">
            <SelectItem value="cpf">CPF (Brazilian Tax ID)</SelectItem>
            <SelectItem value="cnpj">CNPJ (Company Tax ID)</SelectItem>
            <SelectItem value="email">Email</SelectItem>
            <SelectItem value="phone">Phone</SelectItem>
            <SelectItem value="evp">Random Key (EVP)</SelectItem>
          </SelectContent>
        </Select>
      </FormField>

      <FormField
        label="PIX Key"
        required
        error={errors.pix_key}
      >
        <Input
          type="text"
          value={formData.pix_key || ''}
          onChange={(e) => onChange('pix_key', e.target.value)}
          placeholder={getPlaceholder()}
          className={cn("min-h-[44px]", errors.pix_key && "border-destructive")}
          disabled={disabled || !formData.pix_key_type}
        />
        <Text variant="caption" color="muted" className="mt-1">
          {getHelpText()}
        </Text>
      </FormField>

      <FormField
        label="Account Holder Name"
        required
        error={errors.holder_name}
      >
        <Input
          type="text"
          value={formData.holder_name || ''}
          onChange={(e) => onChange('holder_name', e.target.value)}
          placeholder="John Doe"
          className={cn("min-h-[44px]", errors.holder_name && "border-destructive")}
          disabled={disabled}
        />
      </FormField>
    </div>
  );
}

// ============================================
// Helper Components
// ============================================

interface FormFieldProps {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}

function FormField({ label, required, error, children }: FormFieldProps) {
  return (
    <div className="space-y-2">
      <Label className={cn(semanticTokens.typography.label)}>
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      {children}
      {error && (
        <div className="flex items-center gap-1.5 text-destructive">
          <SFIcon icon={sfIcons.warning} className="h-3.5 w-3.5" />
          <Text variant="caption" className="text-destructive">{error}</Text>
        </div>
      )}
    </div>
  );
}

